import { iconMarkup } from '../icons.js';
import { setTooltip } from '../tooltips.js';
import { SettingsDialog, type SettingsTab } from '../settings/settings-dialog.js';
import { bindMuteButton, controlsTab, creditsTab, GAME_KEYS, interfaceTab, pictureTab, soundTab } from '../settings/shared-tabs.js';

export interface GameSettingsState {
  /** Signed in: the Account tab shows. */
  signedIn(): boolean;
  /** In a world: the Room tab shows. */
  inRoom(): boolean;
  /** Developer tools are bound (the Vite dev client): the Developer tab shows. */
  developer(): boolean;
  /** Releases a captured cursor before the modal opens. */
  releaseCursor(): void;
}

/**
 * The game's settings: the shared Picture, Interface, Sound and Controls tabs, then the room and account the player is in, the credits,
 * and the developer tools in the dev client. The application fills the room, account and developer readouts by their ids.
 */
export function mountGameSettings(mount: HTMLElement, state: GameSettingsState) {
  const room: SettingsTab = {
    id: 'room', label: 'Room', icon: 'party', visible: () => state.inRoom(),
    settings: [
      { kind: 'content', render: () => `<p class="settings-readout"><strong id="room-label"></strong><span id="players"></span></p>
        <p id="network-status" class="status"><i></i>Not connected</p><p id="latency" class="settings-readout-dim"></p>` },
      { kind: 'action', id: 'invite', label: 'Copy invite link' }, { kind: 'action', id: 'leave', label: 'Leave room' },
      { kind: 'action', id: 'restart-encounter', label: 'Restart session' },
    ],
  };
  const account: SettingsTab = {
    id: 'account', label: 'Account', icon: 'commander', visible: () => state.signedIn(),
    settings: [{ kind: 'content', render: () => '<p class="settings-readout"><strong id="account"></strong></p>' }, { kind: 'action', id: 'signout', label: 'Sign out' }],
  };
  const developer: SettingsTab = {
    id: 'developer', label: 'Developer', icon: 'hazard', visible: () => state.developer(),
    settings: [
      { kind: 'heading', label: 'World' },
      { kind: 'content', render: () => '<label class="switch"><input id="reveal-map" type="checkbox"> Remove fog of war</label>' },
      { kind: 'heading', label: 'Time of day' },
      { kind: 'content', render: () => `<label class="setting-range"><span class="setting-value">Clock<output id="dev-time-label" for="dev-time"></output></span>
        <input id="dev-time" type="range" min="0" max="1425" step="15" aria-label="Time of day"></label>
        <div class="settings-actions"><button type="button" class="secondary" data-time="360">Dawn</button><button type="button" class="secondary" data-time="720">Noon</button>
        <button type="button" class="secondary" data-time="1080">Dusk</button><button type="button" class="secondary" data-time="0">Midnight</button></div>
        <label class="setting-choice"><span>Clock speed</span><select id="dev-time-scale" aria-label="Clock speed">
          <option value="0">Stopped</option><option value="0.5">0.5×</option><option value="1">1×</option><option value="2">2×</option>
          <option value="5">5×</option><option value="10">10×</option><option value="30">30×</option><option value="60">60×</option>
        </select></label>` },
      { kind: 'heading', label: 'Cheats' },
      { kind: 'action', id: 'dev-restore', label: 'Restore party' },
    ],
  };
  const settings = new SettingsDialog(mount, [pictureTab(), interfaceTab({ display: true, combatLog: true }), soundTab(), controlsTab(GAME_KEYS), room, account, creditsTab(), developer], {
    footer: `<button id="settings-close-app" class="settings-quit" type="button" hidden>${iconMarkup('doorway')}<span>Close NimbleZ</span></button>`,
    opener: document.getElementById('settings-open'), before: () => state.releaseCursor(),
  });
  bindMuteButton(document.getElementById('mute-toggle')!);
  setTooltip(document.getElementById('restart-encounter')!, 'Restart paused', 'Restart session, paused');
  return settings;
}
